import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project, ProjectSettings } from "../../types/project";
import type { Timeline } from "../../types/timeline";
import type { MotionShaderDef } from "../../motion/shaders/types";
import {
  isBuiltinMotionShaderId,
  registerMotionShader,
  unregisterMotionShader,
} from "../../motion/shaders/registry";
import { applyCanvasResize } from "../../layout/canvas-refit";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler } from "../registry";

function fromErrors(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

function invalidParams(message: string, path: string): ValidationError {
  return { code: "INVALID_PARAMS", message, path };
}

// Project fields are readonly to callers. The action pipeline edits the live
// object in place because the store holds one project instance and hands the
// same reference to the renderer.
function setName(project: Project, name: string): void {
  (project as { name: string }).name = name;
}

function setSettings(project: Project, settings: ProjectSettings): void {
  (project as { settings: ProjectSettings }).settings = settings;
}

function setTimeline(project: Project, timeline: Timeline): void {
  (project as { timeline: Timeline }).timeline = timeline;
}

function setGeneratedShaders(
  project: Project,
  shaders: readonly MotionShaderDef[],
): void {
  (
    project as { generatedShaders?: readonly MotionShaderDef[] }
  ).generatedShaders = shaders;
}

function markModified(project: Project): void {
  (project as { modifiedAt: number }).modifiedAt = Date.now();
}

/**
 * Shared by updateSettings and setCanvasBackground, which sat on one fallthrough
 * case in the legacy switch. Both take a partial payload, so there is nothing to
 * check field by field. Only an array can actually trip this: the validator's
 * own entry check has already rejected a missing or non-object params, and an
 * array is truthy and typeof "object".
 */
function requireObjectParams(action: Action): ValidationResult {
  const params: unknown = action.params;
  const errors: ValidationError[] = [];
  if (!params || typeof params !== "object" || Array.isArray(params)) {
    errors.push(invalidParams("Params must be an object", "params"));
  }
  return fromErrors(errors);
}

/**
 * Undoing a creation would mean deleting the project the user is looking at, so
 * the legacy generator returned null and this keeps doing that. The cost is real
 * and is not this handler's to pay: the executor pushes a history entry either
 * way, and ActionHistory refuses to pop an entry with no inverse, so a create
 * sitting in the middle of a session walls off everything under it from undo.
 * Nothing in the editor or the agent dispatches it today, which is why that has
 * never been felt.
 */
const projectCreate: ActionHandler = {
  type: "project/create",

  validate(action: Action): ValidationResult {
    const params = action.params as { name?: unknown; settings?: unknown };
    const errors: ValidationError[] = [];

    if (!params.name || typeof params.name !== "string") {
      errors.push(
        invalidParams(
          "Project name is required and must be a string",
          "params.name",
        ),
      );
    }
    if (!params.settings) {
      errors.push(
        invalidParams("Project settings are required", "params.settings"),
      );
    }

    return fromErrors(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { name: string; settings: ProjectSettings };
    setName(project, params.name);
    setSettings(project, params.settings);
    markModified(project);
  },

  invert(): Action | null {
    return null;
  },
};

const projectRename: ActionHandler = {
  type: "project/rename",

  validate(action: Action): ValidationResult {
    const params = action.params as { name?: unknown };
    const errors: ValidationError[] = [];

    if (!params.name || typeof params.name !== "string") {
      errors.push(
        invalidParams(
          "Project name is required and must be a string",
          "params.name",
        ),
      );
    }

    return fromErrors(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { name: string };
    setName(project, params.name);
    markModified(project);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return createInverseAction(action, "project/rename", {
      name: projectBefore.name,
    });
  },
};

const projectUpdateSettings: ActionHandler = {
  type: "project/updateSettings",

  validate: requireObjectParams,

  apply(action: Action, project: Project): void {
    const previousSize = {
      width: project.settings.width,
      height: project.settings.height,
    };
    const settings = {
      ...project.settings,
      ...(action.params as Partial<ProjectSettings>),
    };
    setSettings(project, settings);
    // Clip placement is stored in canvas terms, so a canvas of a new size
    // leaves every moved, zoomed or slot-confined clip in the wrong place
    // until it is re-fitted. Every factor applied here is a ratio between
    // the two sizes, so the inverse action (this same type, carrying the
    // previous settings) puts the clips back: one history entry covers the
    // size change and the re-fit together.
    applyCanvasResize(project, previousSize, settings);
    markModified(project);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return createInverseAction(action, "project/updateSettings", {
      ...projectBefore.settings,
    });
  },
};

const projectSetCanvasBackground: ActionHandler = {
  type: "project/setCanvasBackground",

  validate: requireObjectParams,

  apply(action: Action, project: Project): void {
    const params = action.params as {
      backgroundFillMode?: "color" | "blur";
      layoutBackgroundColor?: string;
    };
    // A fresh timeline object rather than two field writes, because the editor
    // re-renders off the timeline reference and an in-place edit is invisible
    // to it. Both fields are written unconditionally, so an action that names
    // only one of them clears the other; the inverse carries both back, which
    // is what keeps that round trip honest.
    setTimeline(project, {
      ...project.timeline,
      backgroundFillMode: params.backgroundFillMode,
      layoutBackgroundColor: params.layoutBackgroundColor,
    });
    markModified(project);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return createInverseAction(action, "project/setCanvasBackground", {
      backgroundFillMode: projectBefore.timeline.backgroundFillMode,
      layoutBackgroundColor: projectBefore.timeline.layoutBackgroundColor,
    });
  },
};

const projectRegisterGeneratedShader: ActionHandler = {
  type: "project/registerGeneratedShader",

  validate(action: Action): ValidationResult {
    const def = (action.params as { def?: { id?: unknown; glsl?: unknown } })
      .def;
    const errors: ValidationError[] = [];

    if (
      !def ||
      typeof def !== "object" ||
      typeof def.id !== "string" ||
      typeof def.glsl !== "string"
    ) {
      errors.push(invalidParams("A valid shader def is required", "params.def"));
    }

    return fromErrors(errors);
  },

  apply(action: Action, project: Project): void {
    const def = (action.params as { def: MotionShaderDef }).def;
    // registerMotionShader throws on a built-in id rather than letting a
    // generated look shadow one, so the id is checked before anything is
    // written and the whole action becomes a no-op.
    if (isBuiltinMotionShaderId(def.id)) return;
    const existing: readonly MotionShaderDef[] = project.generatedShaders ?? [];
    const next = [...existing.filter((entry) => entry.id !== def.id), def];
    setGeneratedShaders(project, next);
    markModified(project);
    registerMotionShader(def);
  },

  invert(action: Action): Action | null {
    // Minted even when apply refused a built-in id, exactly as the legacy
    // generator did. Undoing that no-op removes nothing, because neither the
    // project list nor the shader registry holds the built-in under that id.
    const def = (action.params as { def: MotionShaderDef }).def;
    return createInverseAction(action, "project/removeGeneratedShader", {
      shaderId: def.id,
    });
  },
};

const projectRemoveGeneratedShader: ActionHandler = {
  type: "project/removeGeneratedShader",

  validate(action: Action): ValidationResult {
    const params = action.params as { shaderId?: unknown };
    const errors: ValidationError[] = [];

    if (!params.shaderId || typeof params.shaderId !== "string") {
      errors.push(
        invalidParams(
          "shaderId is required and must be a string",
          "params.shaderId",
        ),
      );
    }

    return fromErrors(errors);
  },

  apply(action: Action, project: Project): void {
    const shaderId = (action.params as { shaderId: string }).shaderId;
    const existing: readonly MotionShaderDef[] = project.generatedShaders ?? [];
    setGeneratedShaders(
      project,
      existing.filter((entry) => entry.id !== shaderId),
    );
    markModified(project);
    // Unconditional, as in the legacy switch: the renderer registry is process
    // wide while the list above is per project, so a removal aimed at a def
    // this project never listed still empties the registry, and the invert
    // below has nothing to put back.
    unregisterMotionShader(shaderId);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const shaderId = (action.params as { shaderId: string }).shaderId;
    // Nothing in the project to put back means nothing an inverse could carry:
    // the def itself is the only record of the GLSL, and it is gone.
    const removed = (projectBefore.generatedShaders ?? []).find(
      (def) => def.id === shaderId,
    );
    if (!removed) return null;
    return createInverseAction(action, "project/registerGeneratedShader", {
      def: removed,
    });
  },
};

for (const handler of [
  projectCreate,
  projectRename,
  projectUpdateSettings,
  projectSetCanvasBackground,
  projectRegisterGeneratedShader,
  projectRemoveGeneratedShader,
]) {
  registerActionHandler(handler);
}
